<script setup>
import axios from '@/helpers/axios'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import moment from "moment";
import AppDateTimePicker from "@core/components/app-form-elements/AppDateTimePicker.vue";

definePage({
  meta: {
    layout: 'default',
    role: 'user'
  },
})

const { t } = useI18n()
const items = ref([])
const online = ref(false)
const searchQuery = ref('')
const rowPerPage = ref(9)
const currentPage = ref(1)
const totalPage = ref(1)
const totalItems = ref(0)

const user = ref(useCookie('userData').value)
moment.locale(user.value.locale || 'en')

const selectedVisibility = ref('mine')
const visibilities = [
  { title: t('webcams.mine'), value: 'mine' },
  { title: t('webcams.all'), value: 'all' },
]

const formattedDate = (date) => {
  if (!date) return ''
  return moment(date).fromNow()
}

const getData = () => {
  axios.get('/webcams', {
    params: {
      visibility: selectedVisibility.value,
      online: online.value ? 1 : 0,
      q: searchQuery.value,
      perPage: rowPerPage.value,
      currentPage: currentPage.value,
    }
  }).then(r => {
    items.value = r.data.data
    totalPage.value = r.data.meta.last_page
    totalItems.value = r.data.meta.total
  }).catch(e => {
    console.error(e)
  })
}

getData()

watch([
  selectedVisibility,
  online,
  searchQuery,
  currentPage,
], () => {
  getData()
})

watchEffect(() => {
  if (currentPage.value > totalPage.value)
    currentPage.value = totalPage.value
})


// 👉 Computing pagination data
const paginationData = computed(() => {
  const firstIndex = items.value.length ? (currentPage.value - 1) * rowPerPage.value + 1 : 0
  const lastIndex = items.value.length + (currentPage.value - 1) * rowPerPage.value

  return t('tables.showing', { firstIndex, lastIndex, totalItems: totalItems.value })
})
</script>

<template>
  <div>
    <div class="page-header mb-4">
      <div class="d-flex flex-column flex-sm-row align-center justify-space-between gap-4">
        <div class="flex-grow-1">
          <h2 class="text-h4 font-weight-semibold mb-2 text-high-emphasis">
            {{ $t('webcams.title') }}
          </h2>
          <p class="text-body-1 text-medium-emphasis mb-0">
            {{ $t('webcams.webcams_description') }}
          </p>
        </div>
      </div>
    </div>

    <VRow class="mb-4" align="center">
      <!-- Search Field -->
      <VCol cols="12" md="3">
        <VTextField
          v-model="searchQuery"
          :placeholder="$t('webcams.search_webcam')"
          prepend-inner-icon="tabler-search"
          clearable
        />
      </VCol>

      <VCol cols="12" md="3">
        <AppSelect
          v-model="selectedVisibility"
          :items="visibilities"
          style="max-width: 290px; width: 100%;"
          offset-y
          item-title="title"
          item-value="value"
        />
      </VCol>

      <VCol cols="12" md="3">
        <VCheckbox
          v-model="online"
          :label="$t('webcams.online')"
        />
      </VCol>
    </VRow>

    <div v-if="items.length">
      <VRow  class="match-height">
        <VCol
          v-for="(item, i) in items"
          :key="item.uuid"
          cols="12"
          sm="6"
          lg="4"
        >
          <VCard
            style="border-radius: 16px; position: relative; height: 250px; overflow: hidden;"
            elevation="2"
            class="cursor-pointer group-card"
            :to="item.last_data
              ? { name: 'webcams-uuid', params: { uuid: item.uuid }, query: { last_data: moment(item.last_data).format('YYYY-MM-DD') } }
              : { name: 'webcams-uuid', params: { uuid: item.uuid } }"
          >
            <VImg
              v-if="item.last_image"
              :src="item.last_image"
              alt="Webcam image"
              cover
              height="100%"
              width="100%"
            />
            <div v-else class="d-flex align-center justify-center text-center fill-height">
              <div class="d-flex flex-column align-center justify-center py-10">
                <VIcon icon="tabler-photo-off" size="48" color="grey" class="mb-3" />
                <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
                  {{ $t('webcams.no_images') }}
                </h4>
              </div>
            </div>


            <div
              class="pa-2"
              style="
              position: absolute;
              top: 8px;
              left: 8px;
              background-color: rgba(0, 0, 0, 0.55);
              color: white;
              border-radius: 8px;
              font-weight: 700;
              font-size: 0.9rem;
              backdrop-filter: blur(4px);
            "
            >
              {{ item.name }}
            </div>

            <div
              class="pa-2"
              style="
              position: absolute;
              top: 8px;
              right: 8px;
              color: #fff;
              border-radius: 8px;
              font-size: 0.8rem;
              z-index: 10;
            "
            >
              <VAvatar
                size="34"
                class="ms-n2 border border-white elevation-1"
              >
                <VImg :src="item.user.avatar_thumbnail" cover />
                <VTooltip activator="parent" location="top">
                  {{ item.user.name }}
                </VTooltip>
              </VAvatar>
            </div>

            <div
              class="pa-2"
              style="
              position: absolute;
              bottom: 8px;
              left: 8px;
              background-color: rgba(0, 0, 0, 0.55);
              color: #fff;
              border-radius: 8px;
              font-size: 0.8rem;
              backdrop-filter: blur(4px);
            "
            >
              <VBadge
                dot
                inline
                :color="item.online ? 'primary' : 'error'"
                location="left center"
              />
              <span v-if="item.last_data">
              {{ formattedDate(item.last_data) }}
            </span>
              <span v-else>
              {{ $t('webcams.offline') }}
            </span>
            </div>
          </VCard>
        </VCol>
      </VRow>

      <!-- SECTION Pagination -->
      <div class="align-center gap-4 py-3">
        <!-- 👉 Pagination -->
        <VPagination
          v-model="currentPage"
          size="small"
          :total-visible="5"
          :length="totalPage"
        />
      </div>
    </div>
    <div v-else>
      <div
        class="d-flex flex-column align-center justify-center text-center flex-grow-1 py-10"
      >
        <VIcon icon="tabler-camera-off" size="48" color="grey" class="mb-3" />
        <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
          {{ $t('webcams.no_webcams') }}
        </h4>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-header {
  background: linear-gradient(180deg, var(--v-theme-surface), transparent);
  border-radius: 16px;
  padding: 1.5rem 0rem;
  transition: all 0.25s ease;
}

@media (max-width: 600px) {
  .page-header {
    padding: 1.25rem 1rem;
  }
  .page-header h2 {
    font-size: 1.4rem;
  }
}

.group-card {
  transition: all 0.25s ease;
}

.group-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  border-color: var(--v-theme-primary);
}
</style>
